/**
 * AppShell.tsx — Neues 3-Spalten-Layout
 *
 * Ersetzt die alte schwebende Sidebar.
 * Layout: NavRail (56px) | ContentPanel (400px, optional) | MainArea (rest)
 *
 * Der Potree-Viewer wird NICHT von React gerendert (lebt in index.html).
 * Stattdessen steuert AppShell die CSS-Klassen auf <body>, um den Viewer
 * per CSS zu verschieben / auszublenden.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect, Suspense, lazy } from 'react'
import { Sidebar, OVERLAY_SECTIONS, hasModuleAccess, SECTION_MODULE_MAP, type NavSection } from './Sidebar'
import { ContentPanel } from './ContentPanel'
import { useAnnotationStore } from '../../store/annotations'
import { useAuthStore } from '../../store/auth'
import { useSceneStore } from '../../store/scenes'
import { AnnotationList } from '../annotations/AnnotationList'
import { AnnotationEditor } from '../annotations/AnnotationEditor'
import { AnnotationLayer } from '../annotations/AnnotationLayer'
import { DocumentList } from '../documents/DocumentList'
import { DocReader } from '../documents/DocReader'
import { DocumentEditor } from '../documents/DocumentEditor'
import { AuditList } from '../audits/AuditList'
import { ScenePanel } from '../scenes/ScenePanel'
import { CloudPanel } from '../clouds/CloudPanel'
import { PointCloudUploadPanel } from '../clouds/PointCloudUploadPanel'
import { useControllerStore } from '../../store/controller'
import { PresenceBar } from '../presence/PresenceBar'
import { ViewerSyncOverlay } from '../presence/ViewerSyncOverlay'
import { PaperlessPanel } from '../paperless/PaperlessPanel'
import { BuildingDataPanel } from '../building/BuildingDataPanel'
import { GebaeudePanel } from '../building/GebaeudePanel'
import { AgentChatPanel } from '../agent/AgentChatPanel'
import { PresentationDock } from '../presentation/PresentationDock'
import { AnimationBar } from '../animation/AnimationBar'
import RaumbuchPanel from '../raumbuch/RaumbuchPanel'
import { BoardPanel } from '../board/BoardPanel'
import { apiFetch, projectUrl } from '../../lib/apiBase'

// WhiteboardPanel zieht tldraw + yjs + y-websocket nach (~2 MB).
// Lazy laden, damit der Hauptbundle (und vite optimizeDeps beim Boot)
// nicht durch die schwere Whiteboard-Stack belastet wird.
const WhiteboardPanel = lazy(() =>
  import('../whiteboard/WhiteboardPanel').then((m) => ({ default: m.WhiteboardPanel }))
)

// ErrorBoundary für tldraw/tiptap v3 Kompatibilitätsprobleme
class WhiteboardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2em', color: '#999', textAlign: 'center' }}>
          <p style={{ fontSize: 14, marginBottom: 8 }}>Whiteboard konnte nicht geladen werden</p>
          <p style={{ fontSize: 12, color: '#666' }}>{this.state.error.message}</p>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 12, padding: '6px 16px', background: '#333', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >Erneut versuchen</button>
        </div>
      )
    }
    return this.props.children
  }
}
import { PlanDropZone } from '../clouds/PlanDropZone'
import { SemanticZoomOverlay, type SemanticZoomOverlayHandle } from '../SemanticZoomOverlay'
import { CssDevEditor } from '../dev/CssDevEditor'
import { captureAndStoreSceneThumbnail } from '../../lib/thumbnailService'
import { captureViewerThumbnailPair } from '../../lib/captureViewerThumbnail'
import { captureViewerThumbnailMediumHigh } from '../../lib/captureViewerThumbnail'
import { useSemanticZoom } from '../../hooks/useSemanticZoom'
// theme.ts no longer used — Ghost CSS handles styling

declare global {
  interface Window {
    viewer: any
    currentCloudConfig: { id: string; name: string } | null
    dinReportOpen?: () => void
    dinReportClose?: () => void
    dinFormOpen?: () => void
    dinFormClose?: () => void
    dinHasResult?: () => boolean
    dinOpenEmmissionssenkenSection?: () => void
  }
}

/**
 * DinPanel — CO₂-Formular + Ergebnis im ContentPanel.
 *
 * Layout (vertikal):
 *   ┌─ Eingabe ────────────── [∨] ─┐  ← einklappbar
 *   │  #din-ic-mount               │
 *   └──────────────────────────────┘
 *   ┌─ Ergebnis ───────────────────┐  ← erscheint nach Berechnung
 *   │  #din-rp-mount               │
 *   └──────────────────────────────┘
 */
function DinPanel() {
  const [formOpen, setFormOpen] = React.useState(true)
  const [hasResult, setHasResult] = React.useState(
    () => typeof window.dinHasResult === 'function' && window.dinHasResult()
  )

  // Formular beim ersten Mount öffnen
  useEffect(() => {
    if (typeof window.dinFormOpen === 'function') window.dinFormOpen()
    return () => {
      if (typeof window.dinFormClose === 'function') window.dinFormClose()
    }
  }, [])

  // Auf Berechnungsergebnis warten (din-form.js → AppShell → din-result-ready)
  useEffect(() => {
    const handleResult = () => {
      setHasResult(true)
      setFormOpen(false)        // Eingabe einklappen nach Berechnung
    }
    window.addEventListener('din-result-ready', handleResult)
    return () => window.removeEventListener('din-result-ready', handleResult)
  }, [])

  // dinReportOpen NACH React-Render aufrufen, damit #din-rp-mount im DOM ist
  useEffect(() => {
    if (hasResult && typeof window.dinReportOpen === 'function') {
      window.dinReportOpen()
    }
  }, [hasResult])

  // Beim Aufklappen: dinFormOpen() stellt sicher dass din-ic-closed entfernt wird
  // (din-form.js setzt din-ic-closed nach jeder Berechnung)
  const handleToggleForm = React.useCallback(() => {
    setFormOpen(prev => {
      const next = !prev
      if (next && typeof window.dinFormOpen === 'function') window.dinFormOpen()
      return next
    })
  }, [])

  return (
    <div className="din-panel">
      {/* ── Eingabe (nur vor Berechnung sichtbar) ─────────────── */}
      {!hasResult && (
        <div className="din-panel__section">
          <div id="din-ic-mount" />
        </div>
      )}

      {/* ── Ergebnis (sichtbar nach Berechnung) ──────────────── */}
      {hasResult && (
        <div className="din-panel__section din-panel__section--result">
          <div className="din-panel__fold-header din-panel__fold-header--static">
            <span>Ergebnis</span>
          </div>
          <div id="din-rp-mount" />
        </div>
      )}
    </div>
  )
}

/**
 * DinResultMount — Zeigt nur die Ergebnistabelle (din-rp) im ContentPanel.
 *
 * Flow:
 * 1. CO₂-Klick → DinInputMount (Formular embedded im ContentPanel)
 * 2. Nach Berechnung → dinOpenEmmissionssenkenSection() → section bleibt 'emmissionssenken'
 * 3. ContentPanel zeigt DinInputMount (Formular bleibt sichtbar, Report-Panel öffnet links)
 */
function DinResultMount() {
  const rpMountRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Ergebnis ins Panel mounten
    if (typeof window.dinReportOpen === 'function') {
      window.dinReportOpen()
    }
  }, [])

  return (
    <div
      ref={rpMountRef}
      id="din-rp-mount"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    />
  )
}

/**
 * AuditFormMount — Embeddet den Audit-Dialog (aus script.js) im ContentPanel.
 * showAuditDialog() erkennt #audit-form-mount und rendert dort rein.
 */
/**
 * AuditPlacementHint — Zeigt Hinweis "Klicken Sie auf einen Punkt…"
 * und wartet auf Potree-Klick → extractDetail → öffnet Formular.
 */
function AuditPlacementHint({ onPlaced, onCancel }: { onPlaced: () => void; onCancel: () => void }) {
  const onPlacedRef = useRef(onPlaced)
  onPlacedRef.current = onPlaced

  useEffect(() => {
    // Listen for click on Potree canvas
    const handler = (e: MouseEvent) => {
      const viewer = (window as any).viewer
      const clipVol = (window as any).currentClipVolume
      if (!viewer || !clipVol) return

      // Potree erwartet Mauskoordinaten relativ zum Canvas (nicht clientX/Y)
      const rect = viewer.renderer.domElement.getBoundingClientRect()
      const mouseRel = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }

      // Potree-Picking (gleiche Methode wie script.js)
      let worldPoint: any = null
      try {
        const pt = (window as any).Potree?.Utils?.getMousePointCloudIntersection?.(
          mouseRel,
          viewer.scene.getActiveCamera(),
          viewer,
          viewer.scene.pointclouds,
          { pickClipped: true }
        )
        if (pt) worldPoint = pt.location ? pt.location.clone() : pt.clone?.() || pt
      } catch { /* */ }

      // Fallback: Kamera-Pivot verwenden
      if (!worldPoint) {
        const view = viewer.scene.view
        const pivot = view.getPivot ? view.getPivot() : view.position
        worldPoint = { x: pivot.x, y: pivot.y, z: pivot.z }
      }

      // Detail-Clipping-Box am geklickten Punkt erstellen
      const extractDetail = (window as any).extractDetail
      if (extractDetail && worldPoint) {
        const THREE = (window as any).THREE
        const v = THREE && !(worldPoint instanceof THREE.Vector3)
          ? new THREE.Vector3(worldPoint.x, worldPoint.y, worldPoint.z)
          : worldPoint
        extractDetail(v)
      }

      onPlacedRef.current()
    }

    // Attach to Potree's render area
    const v = (window as any).viewer
    const canvas = v?.renderer?.domElement || document.querySelector('.potree_container canvas, .potree_container .potree_render_area')
    const target = canvas || document.querySelector('.potree_container')
    if (target) {
      target.addEventListener('click', handler as EventListener, { once: true })
    }

    return () => {
      if (target) target.removeEventListener('click', handler as EventListener)
    }
  }, [])

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: '2em 1.5em',
      textAlign: 'center',
      gap: '1em',
    }}>
      {/* Crosshair icon */}
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="2" x2="12" y2="6"/>
        <line x1="12" y1="18" x2="12" y2="22"/>
        <line x1="2" y1="12" x2="6" y2="12"/>
        <line x1="18" y1="12" x2="22" y2="12"/>
      </svg>
      <div>
        <p style={{ fontSize: '0.75em', fontWeight: 600, color: '#111827', margin: '0 0 0.4em' }}>
          Punkt auswählen
        </p>
        <p style={{ fontSize: '0.625em', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
          Klicken Sie auf einen Punkt in der Punktwolke, um den herum die Detail-Clipping-Box erstellt wird.
        </p>
      </div>
      <button
        onClick={onCancel}
        style={{
          marginTop: '0.5em',
          padding: '0.4em 1em',
          fontSize: '0.625em',
          fontWeight: 500,
          color: '#6b7280',
          background: 'none',
          border: '1px solid #e5e7eb',
          borderRadius: '0.375em',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Abbrechen
      </button>
    </div>
  )
}

function AuditFormMount({ auditId, onClose }: { auditId: string; onClose: () => void }) {
  const mountRef = useRef<HTMLDivElement>(null)
  // Ref für onClose, damit der Event-Listener nie stale ist
  // und onClose nicht als useEffect-Dependency gebraucht wird.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const el = mountRef.current
    if (!el) return

    const showAuditDialog = (window as any).showAuditDialog as
      ((data?: any, onSave?: (d: any) => void) => void) | undefined
    const editAudit = (window as any).editAudit as ((id: string) => void) | undefined

    if (!showAuditDialog) {
      console.warn('[AuditFormMount] showAuditDialog noch nicht geladen')
      return
    }

    if (auditId === 'new') {
      showAuditDialog()
    } else if (editAudit) {
      editAudit(auditId)
    }

    const onDialogClose = () => {
      onCloseRef.current()
    }
    window.addEventListener('audit:dialogClosed', onDialogClose)

    return () => {
      window.removeEventListener('audit:dialogClosed', onDialogClose)
      const dialog = el.querySelector('.audit-dialog')
      if (dialog) dialog.remove()
    }
  }, [auditId]) // onClose bewusst nicht in deps — über Ref gelöst

  return (
    <div
      ref={mountRef}
      id="audit-form-mount"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    />
  )
}

// ── ViewLayerPill — 3-Stufen-Schalter: Cloud / Both / Rooms ──
type ViewLayerMode = 'cloud' | 'both' | 'rooms';
const VIEW_LAYER_ICONS: Record<ViewLayerMode, React.ReactNode> = {
  cloud: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" opacity="0.55">
      <circle cx="2" cy="2" r="1.1"/><circle cx="6" cy="2" r="1.1"/><circle cx="10" cy="2" r="1.1"/>
      <circle cx="2" cy="6" r="1.1"/><circle cx="6" cy="6" r="1.1"/><circle cx="10" cy="6" r="1.1"/>
      <circle cx="2" cy="10" r="1.1"/><circle cx="6" cy="10" r="1.1"/><circle cx="10" cy="10" r="1.1"/>
    </svg>
  ),
  both: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 2.5">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
    </svg>
  ),
  rooms: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="12" y1="3" x2="12" y2="21"/>
    </svg>
  ),
};
const VIEW_LAYER_MODES: { id: ViewLayerMode; label: string }[] = [
  { id: 'cloud', label: 'Punktwolke' },
  { id: 'both',  label: 'Überlagert' },
  { id: 'rooms', label: 'Raumbuch' },
];

function ViewLayerPill() {
  const [mode, setMode] = useState<ViewLayerMode>('cloud');

  const handleChange = useCallback((m: ViewLayerMode) => {
    setMode(m);
    (window as any).setViewLayerMode?.(m);
  }, []);

  return (
    <div className="view-layer-pill">
      {VIEW_LAYER_MODES.map(m => (
        <button
          key={m.id}
          className={`view-layer-pill__btn${mode === m.id ? ' view-layer-pill__btn--active' : ''}`}
          onClick={() => handleChange(m.id)}
        >
          {VIEW_LAYER_ICONS[m.id]}
          <span>{m.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── BuildingFloatDialog — Gebäudedaten als Floating-Fenster (wie Audit-Dialog) ──
function BuildingFloatDialog({ projectId, onClose }: { projectId: string | null; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [minimized, setMinimized] = useState(true)

  // Drag-Logik
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const handle = dialog.querySelector('#building-float-drag') as HTMLElement
    if (!handle) return

    let dragging = false, dx = 0, dy = 0

    const onMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('.building-float__minimize')) return
      dragging = true
      const rect = dialog.getBoundingClientRect()
      dx = e.clientX - rect.left
      dy = e.clientY - rect.top
      handle.style.cursor = 'grabbing'
      e.preventDefault()
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return
      dialog.style.left = (e.clientX - dx) + 'px'
      dialog.style.top = (e.clientY - dy) + 'px'
      dialog.style.right = 'auto'
      dialog.style.transform = 'none'
    }
    const onMouseUp = () => {
      dragging = false
      handle.style.cursor = 'grab'
    }

    handle.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      handle.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const address = useAnnotationStore(s => s.buildingData?.fields?.ort) || ''

  return (
    <div className={`building-float${minimized ? ' building-float--minimized' : ''}`} ref={dialogRef}>
      <div className="building-float__header" id="building-float-drag">
        {/* Row 1: Title + Address + Minimize */}
        <div className="building-float__title-row">
          <div className="building-float__title-group">
            <span className="building-float__title">Gebäude</span>
            {address && <span className="building-float__address">{address}</span>}
          </div>
          <button
            className="building-float__minimize"
            onClick={() => setMinimized(m => !m)}
            title={minimized ? 'Maximieren' : 'Minimieren'}
          >
            {minimized ? (
              /* maximize-2: Pfeile nach außen */
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            ) : (
              /* minimize-2: Pfeile nach innen */
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20"/>
                <polyline points="20 10 14 10 14 4"/>
                <line x1="14" y1="10" x2="21" y2="3"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            )}
          </button>
        </div>
        {/* Row 2: ViewLayerPill */}
        <ViewLayerPill />
      </div>
      {!minimized && (
        <div className="building-float__body">
          <GebaeudePanel />
        </div>
      )}
    </div>
  )
}

export function AppShell() {
  const viewerRef = useRef<any>(null)
  const overlayRef = useRef<SemanticZoomOverlayHandle>(null)

  // ── Navigation State ─────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState<NavSection | null>('gebaeude')
  /** For detail views: which item ID is being viewed (e.g. document ID) */
  const [detailId, setDetailId] = useState<string | null>(null)
  /** For Docs section: selected paperless doc ID for preview */
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [selectedDocSummary, setSelectedDocSummary] = useState<string | null>(null)

  /** For Audit section: 'new' = create, audit-id = edit, null = list */
  const [auditEditId, setAuditEditId] = useState<string | null>(null)

  const [currentCloudId, setCurrentCloudId] = useState<string | null>(null)
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null)
  const [animActive, setAnimActive] = useState(false)
  const currentProject = useAuthStore(s => s.currentProject)
  const [projectCloudIds, setProjectCloudIds] = useState<string[]>([])
  const [zoomOverlayOpen, setZoomOverlayOpen] = useState(false)

  /** Overlay section (Scene / Punktwolke) — independent from main panel */
  const [overlaySection, setOverlaySection] = useState<NavSection | null>(null)

  /** True while Whiteboard→Annotation overlay is active (Potree + markers visible) */
  const [annotationOverlayActive, setAnnotationOverlayActive] = useState(false)

  // ── Semantic Zoom ────────────────────────────────────────────────────────
  const { tier, distance, layerState, fadeFactors } = useSemanticZoom({ viewerRef })

  // ── Annotation Store ─────────────────────────────────────────────────────
  const loadFromServer = useAnnotationStore(s => s.loadFromServer)
  const loadBuildingData = useAnnotationStore(s => s.loadBuildingData)
  const activeDocumentId = useAnnotationStore(s => s.editor.activeDocumentId)
  const activeAnnotationId = useAnnotationStore(s => s.editor.activeAnnotationId)
  const editorMode = useAnnotationStore(s => s.editor.mode)
  const annotations = useAnnotationStore(s => s.annotations)
  const documents = useAnnotationStore(s => s.documents)
  const filterType = useAnnotationStore(s => s.filterType)
  const searchQuery = useAnnotationStore(s => s.searchQuery)
  const startPlacing = useAnnotationStore(s => s.startPlacingAnnotation)
  const createDocument = useAnnotationStore(s => s.createDocument)
  const openDocumentEditor = useAnnotationStore(s => s.openDocumentEditor)
  const openDocumentViewer = useAnnotationStore(s => s.openDocumentViewer)
  const closeEditor = useAnnotationStore(s => s.closeEditor)

  // ── Scene Store ──────────────────────────────────────────────────────────
  const sceneCreateScene = useSceneStore(s => s.createScene)
  const sceneLoadFromServer = useSceneStore(s => s.loadFromServer)
  const sceneActivate = useSceneStore(s => s.activateScene)
  const sceneActivateStart = useSceneStore(s => s.activateStartScene)
  const sceneList = useSceneStore(s => s.scenes)

  // ── Whiteboard-Embed-Events: Szene/Doc aus einer Embed-Card öffnen ───────
  // Die Custom-Shapes (orto-scene, orto-doc) feuern beim Klick auf
  // "→ öffnen" ein CustomEvent. Hier fangen wir es ab und führen die
  // entsprechende Aktion aus: Whiteboard schließen + Scene laden bzw.
  // Doc-Reader öffnen.
  useEffect(() => {
    function handleOpenScene(e: Event) {
      const sceneId = (e as CustomEvent).detail?.sceneId
      if (!sceneId) return
      // Whiteboard verlassen, zurück in den Viewer
      setActiveSection(null)
      setSelectedDocId(null)
      // Scene asynchron aktivieren (gibt dem Viewer eine Frame Zeit zum mounten)
      requestAnimationFrame(() => {
        try { sceneActivate(sceneId) } catch (err) { console.warn('[wb] activateScene failed', err) }
      })
    }
    function handleOpenDoc(e: Event) {
      const docId = (e as CustomEvent).detail?.docId
      if (!docId) return
      // In den Docs-Bereich wechseln und den Reader öffnen
      setActiveSection('docs')
      setSelectedDocId(docId)
      setSelectedDocSummary(null)
      openDocumentViewer(docId)
    }

    // ── Whiteboard → Scene Thumbnail Capture ─────────────────────────────
    // Workflow:
    //   1. Whiteboard schließen (damit der Viewer sichtbar ist)
    //   2. Scene aktivieren → Potree lädt camera/clipBox
    //   3. Nach 1500ms (Render-Settle) Snapshot der WebGL-Canvas erstellen
    //   4. PATCH /api/v1/scenes/{id} mit thumbnail (data:image/jpeg;base64,…)
    //   5. Whiteboard wieder öffnen
    //   6. Erfolg/Fehler per Event an die Lightbox zurückmelden
    async function handleCaptureScene(e: Event) {
      const sceneId = (e as CustomEvent).detail?.sceneId
      if (!sceneId) return

      // 1. Whiteboard verlassen — Body-Klasse `shell-viewer-hidden` wird
      //    entfernt, .potree_container darf wieder rendern.
      setActiveSection(null)

      // 2. Layout-Settle: Potree liest seine Größe nicht reaktiv. Wir
      //    warten auf zwei Frames + dispatchen ein resize-Event, damit
      //    der WebGL-Renderer den neuen Viewport übernimmt. Sonst rendert
      //    Potree in einen 0×0-Framebuffer (Attachment-zero-size Errors).
      await new Promise<void>((res) => requestAnimationFrame(() => res()))
      await new Promise<void>((res) => requestAnimationFrame(() => res()))
      window.dispatchEvent(new Event('resize'))
      try {
        const v = (window as any).viewer
        // Potree-spezifische Resize-Methoden, falls vorhanden
        if (v?.renderer && v?.renderArea) {
          const w = v.renderArea.clientWidth || window.innerWidth
          const h = v.renderArea.clientHeight || window.innerHeight
          v.renderer.setSize(w, h)
        }
      } catch (err) {
        console.warn('[wb] viewer.renderer.setSize fehlgeschlagen', err)
      }

      // 3. Scene aktivieren
      try {
        sceneActivate(sceneId)
      } catch (err) {
        console.warn('[wb] activateScene fehlgeschlagen', err)
      }

      // 4. Render-Settle abwarten (Potree braucht Zeit für Camera-Tweens
      //    + LRU-Decoding der Punktwolken)
      await new Promise<void>((res) => setTimeout(res, 1800))

      // 5. Canvas-Snapshot via sceneThumbnailService (Potree-Render + zwei Groessen)
      try {
        const result = captureAndStoreSceneThumbnail(sceneId)
        if (!result) throw new Error('Viewer-Canvas nicht verfügbar oder leer')
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err)
        console.error('[wb] Thumbnail-Capture fehlgeschlagen:', message)
        window.dispatchEvent(
          new CustomEvent('orto:scene-thumbnail-failed', {
            detail: { sceneId, message },
          }),
        )
      }

      // 7. Whiteboard wieder öffnen
      requestAnimationFrame(() => setActiveSection('whiteboard'))
    }

    // ── Whiteboard-Overlay: Potree im Overlay sichtbar machen ───────────
    function handleActivateViewer(e: Event) {
      const detail = (e as CustomEvent).detail as { type: string; id: string }
      if (!detail) return
      // Potree-Container sichtbar machen und ueber Whiteboard heben
      document.body.classList.remove('shell-viewer-hidden')
      document.body.classList.add('shell-viewer-overlay')

      if (detail.type === 'scene') {
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'))
          // Whiteboard-Overlay: animierte Kamerafahrt vom vorherigen View
          // zur Ziel-Scene statt sofortigem Snap.
          try { sceneActivate(detail.id, { animate: true, duration: 1500 }) }
          catch (err) { console.warn('[wb] activateScene failed', err) }
        })
      } else if (detail.type === 'audit') {
        // Audits benutzen navigateToAuditClip (script.js) statt sceneActivate
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'))
          // Zuerst pruefen ob Audit bereits in _auditObjects geladen ist
          const auditObj = (window as any)._auditObjects?.find((o: any) => o.id === detail.id)
          if (auditObj?.clipSnapshot) {
            try { (window as any).navigateToAuditClip(auditObj.clipSnapshot, detail.id) } catch (err) { console.warn('[wb] navigateToAuditClip failed', err) }
          } else {
            // Audit-Daten von API laden und clipSnapshot anwenden
            apiFetch(projectUrl(`/audits/${encodeURIComponent(detail.id)}`))
              .then((r) => r.ok ? r.json() : null)
              .then((json) => {
                const d = json?.data || json
                if (d?.clipSnapshot && (window as any).navigateToAuditClip) {
                  (window as any).navigateToAuditClip(d.clipSnapshot, detail.id)
                }
              })
              .catch((err) => console.warn('[wb] audit fetch for clip failed', err))
          }
        })
      } else if (detail.type === 'note') {
        // Annotation: Fly-To + Clipping-Box + Popup-Marker oeffnen
        document.body.classList.add('shell-annotation-overlay')
        setAnnotationOverlayActive(true)
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'))

          const annotation = useAnnotationStore.getState().annotations.find(a => a.id === detail.id)
          if (!annotation) { console.warn('[wb] annotation not found:', detail.id); return }

          // Fly-To: Kamera zur Annotation fliegen
          if (annotation.camera && viewerRef.current) {
            const viewer = viewerRef.current
            const cam = annotation.camera
            const THREE = (window as any).Potree?.THREE
            if (THREE) {
              const view = viewer.scene.view
              const startPos = { x: view.position.x, y: view.position.y, z: view.position.z }
              const pivot = view.getPivot?.() ?? view.position
              const startTarget = { x: pivot.x, y: pivot.y, z: pivot.z }
              const dur = cam.animationDuration ?? 800
              const startTime = performance.now()

              function easeInOut(t: number) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t }
              function lerp(a: number, b: number, t: number) { return a + (b - a) * t }

              function animate() {
                const raw = Math.min((performance.now() - startTime) / dur, 1)
                const t = easeInOut(raw)
                viewer.scene.view.position.x = lerp(startPos.x, cam.position.x, t)
                viewer.scene.view.position.y = lerp(startPos.y, cam.position.y, t)
                viewer.scene.view.position.z = lerp(startPos.z, cam.position.z, t)
                viewer.scene.view.lookAt?.(new THREE.Vector3(
                  lerp(startTarget.x, cam.target.x, t),
                  lerp(startTarget.y, cam.target.y, t),
                  lerp(startTarget.z, cam.target.z, t),
                ))
                if (raw < 1) requestAnimationFrame(animate)
              }
              requestAnimationFrame(animate)

              // Clipping-Box: 10m Kubus um den Target-Punkt
              const clipVolume = (window as any).currentClipVolume
              if (clipVolume) {
                clipVolume.position.set(cam.target.x, cam.target.y, cam.target.z)
                clipVolume.scale.set(10, 10, 10)
                clipVolume.rotation.set(0, 0, 0)
                clipVolume.update?.()
              }
            }
          }

          // Popup-Marker oeffnen (nach kurzem Delay damit Kamera-Flug sichtbar ist)
          setTimeout(() => {
            useAnnotationStore.getState().setSelectedAnnotation(detail.id)
          }, 400)
        })
      }
    }
    function handleDeactivateViewer() {
      // Potree-Container wieder verstecken und z-index zuruecksetzen
      document.body.classList.add('shell-viewer-hidden')
      document.body.classList.remove('shell-viewer-overlay')
      document.body.classList.remove('shell-annotation-overlay')
      setAnnotationOverlayActive(false)
      // Popup-Marker schliessen falls noch offen
      useAnnotationStore.getState().setSelectedAnnotation(null)
    }

    function handleActivateAnnotation(e: Event) {
      const detail = (e as CustomEvent).detail as { annotationId: string }
      if (!detail?.annotationId) return

      const annotation = useAnnotationStore.getState().annotations.find(a => a.id === detail.annotationId)
      useAnnotationStore.getState().openAnnotationView(detail.annotationId)
      setActiveSection('notes')

      // Fly-To: Kamera zur Annotation fliegen + Clipping-Box 10m um Target
      if (annotation?.camera && viewerRef.current) {
        const viewer = viewerRef.current
        const cam = annotation.camera
        const THREE = (window as any).Potree?.THREE
        if (THREE) {
          // Animierter Kamera-Flug
          const view = viewer.scene.view
          const startPos = { x: view.position.x, y: view.position.y, z: view.position.z }
          const pivot = view.getPivot?.() ?? view.position
          const startTarget = { x: pivot.x, y: pivot.y, z: pivot.z }
          const dur = cam.animationDuration ?? 800
          const startTime = performance.now()

          function easeInOut(t: number) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t }
          function lerp(a: number, b: number, t: number) { return a + (b - a) * t }

          function animate() {
            const raw = Math.min((performance.now() - startTime) / dur, 1)
            const t = easeInOut(raw)
            viewer.scene.view.position.x = lerp(startPos.x, cam.position.x, t)
            viewer.scene.view.position.y = lerp(startPos.y, cam.position.y, t)
            viewer.scene.view.position.z = lerp(startPos.z, cam.position.z, t)
            viewer.scene.view.lookAt?.(new THREE.Vector3(
              lerp(startTarget.x, cam.target.x, t),
              lerp(startTarget.y, cam.target.y, t),
              lerp(startTarget.z, cam.target.z, t),
            ))
            if (raw < 1) requestAnimationFrame(animate)
          }
          requestAnimationFrame(animate)

          // Clipping-Box: 10m Kubus um den Target-Punkt
          const clipVolume = (window as any).currentClipVolume
          if (clipVolume) {
            clipVolume.position.set(cam.target.x, cam.target.y, cam.target.z)
            clipVolume.scale.set(10, 10, 10)
            clipVolume.rotation.set(0, 0, 0)
            clipVolume.update?.()
          }
        }
      }
    }

    window.addEventListener('orto:open-scene', handleOpenScene)
    window.addEventListener('orto:open-doc', handleOpenDoc)
    window.addEventListener('orto:capture-scene-thumbnail', handleCaptureScene)
    window.addEventListener('orto:activate-viewer', handleActivateViewer)
    window.addEventListener('orto:deactivate-viewer', handleDeactivateViewer)
    // Info-Button: Annotation-View öffnen ohne Fly-To
    function handleShowAnnotation(e: Event) {
      const detail = (e as CustomEvent).detail as { annotationId: string }
      if (!detail?.annotationId) return
      useAnnotationStore.getState().openAnnotationView(detail.annotationId)
      setActiveSection('notes')
    }

    window.addEventListener('orto:activate-annotation', handleActivateAnnotation)
    window.addEventListener('orto:show-annotation', handleShowAnnotation)

    // Animation-Modus: Dock ausblenden
    const handleAnimActivated = () => setAnimActive(true)
    const handleAnimDeactivated = () => setAnimActive(false)
    window.addEventListener('orto:animActivated', handleAnimActivated)
    window.addEventListener('orto:animDeactivated', handleAnimDeactivated)

    return () => {
      window.removeEventListener('orto:open-scene', handleOpenScene)
      window.removeEventListener('orto:open-doc', handleOpenDoc)
      window.removeEventListener('orto:capture-scene-thumbnail', handleCaptureScene)
      window.removeEventListener('orto:activate-viewer', handleActivateViewer)
      window.removeEventListener('orto:deactivate-viewer', handleDeactivateViewer)
      window.removeEventListener('orto:activate-annotation', handleActivateAnnotation)
      window.removeEventListener('orto:show-annotation', handleShowAnnotation)
      window.removeEventListener('orto:animActivated', handleAnimActivated)
      window.removeEventListener('orto:animDeactivated', handleAnimDeactivated)
    }
  }, [sceneActivate, openDocumentViewer])

  // ── Viewer + Cloud Init ──────────────────────────────────────────────────
  useEffect(() => {
    loadFromServer()
    sceneLoadFromServer()

    function checkViewer() {
      if (window.viewer) viewerRef.current = window.viewer
      const cloudId = window.currentCloudConfig?.id ?? null
      if (cloudId && cloudId !== currentCloudId) setCurrentCloudId(cloudId)
      const projectId: string | null = (window as any).currentProject?.id ?? null
      setCurrentProjectId(prev => prev === projectId ? prev : projectId)
      const pIds: string[] = (window as any).currentProject?.cloudIds ?? []
      setProjectCloudIds(prev => {
        const joined = pIds.join(',')
        return prev.join(',') === joined ? prev : pIds
      })
    }

    checkViewer()
    const interval = setInterval(checkViewer, 500)

    const handleCloudChange = (e: CustomEvent) => {
      setCurrentCloudId(e.detail?.cloudId ?? window.currentCloudConfig?.id ?? null)
      viewerRef.current = window.viewer
      const projectId: string | null = (window as any).currentProject?.id ?? null
      setCurrentProjectId(projectId)
      const pIds: string[] = (window as any).currentProject?.cloudIds ?? []
      setProjectCloudIds(pIds)
    }
    window.addEventListener('potree:cloudChanged', handleCloudChange as EventListener)

    return () => {
      clearInterval(interval)
      window.removeEventListener('potree:cloudChanged', handleCloudChange as EventListener)
    }
  }, [loadFromServer, sceneLoadFromServer])

  // ── BuildingData laden, sobald Projekt bekannt ────────────────────────────
  useEffect(() => {
    if (currentProjectId) loadBuildingData(currentProjectId)
  }, [currentProjectId, loadBuildingData])

  // ── Expose _activateStartScene globally (always mounted, not just when ScenePanel is open)
  useEffect(() => {
    ;(window as any)._activateStartScene = (projectId?: string) => {
      sceneActivateStart(projectId)
    }
    return () => { delete (window as any)._activateStartScene }
  }, [sceneActivateStart])

  // ── Projekt-Wechsel → Gebäude-Tab aktivieren + Scenes laden ─────────────
  const _prevProjectId = useRef<string | null>(null)
  useEffect(() => {
    if (!currentProjectId) return
    // Bei Projektwechsel immer Gebäude-Tab öffnen (→ triggert Startszene)
    if (_prevProjectId.current !== null && _prevProjectId.current !== currentProjectId) {
      setActiveSection('gebaeude')
    }
    _prevProjectId.current = currentProjectId
    sceneLoadFromServer()
  }, [currentProjectId, sceneLoadFromServer])

  // ── Projekt öffnen → Startszene automatisch aktivieren.
  //     Feuert sobald für das aktive Projekt eine Scene mit isStartScene
  //     im Store existiert UND der Viewer bereit ist (restoreFullViewerState vorhanden).
  //     Wird pro (Projekt × Startszene-ID) nur einmal ausgeführt, sodass ein
  //     manueller Scene-Wechsel erhalten bleibt.
  const _startSceneApplied = useRef<string | null>(null) // "<projectId>:<sceneId>"
  useEffect(() => {
    if (activeSection !== 'gebaeude') return
    if (!currentProjectId) return
    const startScene = sceneList.find(sc => sc.isStartScene && sc.projectId === currentProjectId)
    if (!startScene) return
    const key = `${currentProjectId}:${startScene.id}`
    if (_startSceneApplied.current === key) return

    // Viewer muss bereit sein (restoreFullViewerState wird von script.js exponiert)
    if (!(window as any).restoreFullViewerState) {
      // Viewer noch nicht bereit → retry nach kurzer Verzögerung
      const timer = setTimeout(() => {
        if ((window as any).restoreFullViewerState && _startSceneApplied.current !== key) {
          _startSceneApplied.current = key
          console.log('[orto] Startszene aktiviert (delayed):', startScene.id)
          sceneActivateStart(currentProjectId)
        }
      }, 1500)
      return () => clearTimeout(timer)
    }

    _startSceneApplied.current = key
    console.log('[orto] Startszene aktiviert:', startScene.id)
    sceneActivateStart(currentProjectId)
  }, [activeSection, currentProjectId, sceneList, sceneActivateStart])



  // ── Body-Klassen für Potree-Layout ───────────────────────────────────────
  useEffect(() => {
    const body = document.body

    // Ensure old doc-panel class is always removed (AppShell owns layout now)
    body.classList.remove('doc-panel-active')

    // Sidebar expanded state (any section active → wider sidebar)
    if (activeSection) {
      body.classList.add('shell-sidebar-expanded')
    } else {
      body.classList.remove('shell-sidebar-expanded')
    }

    // Set layout classes — panel stays open even when doc reader is active
    // Gebäudedaten/Raumbuch: Viewer bleibt vollflächig
    if (activeSection && activeSection !== 'gebaeude' && activeSection !== 'raumbuch') {
      body.classList.add('shell-panel-open')
    } else {
      body.classList.remove('shell-panel-open')
    }

    // When "docs" section is active and a doc is selected, hide the viewer
    // Also hide viewer when whiteboard is active (fullscreen takeover)
    if ((activeSection === 'docs' && selectedDocId) || activeSection === 'whiteboard') {
      body.classList.add('shell-viewer-hidden')
    } else {
      body.classList.remove('shell-viewer-hidden')
    }

    // Annotation-Overlay aufräumen wenn Section wechselt (z.B. User navigiert per Sidebar weg)
    if (activeSection !== 'whiteboard') {
      setAnnotationOverlayActive(false)
      document.body.classList.remove('shell-annotation-overlay')
    }

    // Widen content panel when DocumentEditor is inline-active
    if (activeSection === 'reports' && detailId && activeDocumentId) {
      body.classList.add('shell-editor-active')
    } else {
      body.classList.remove('shell-editor-active')
    }

    // Potree-Canvas-Größe neu berechnen lassen (nach CSS-Transition)
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 350)

    return () => {
      body.classList.remove('shell-panel-open', 'shell-viewer-hidden', 'doc-panel-active', 'shell-editor-active', 'shell-sidebar-expanded')
      clearTimeout(timer)
      // Canvas zurücksetzen nach Unmount
      setTimeout(() => window.dispatchEvent(new Event('resize')), 350)
    }
  }, [activeSection, selectedDocId, detailId, activeDocumentId])

  // ── Expose activeSection to window (for script.js audit/annotation visibility) ──
  useEffect(() => {
    ;(window as any).activeNavSection = activeSection
  }, [activeSection])

  // ── Globale Funktionen (Compat) ──────────────────────────────────────────
  useEffect(() => {
    ;(window as any).openDokumentenablage = () => {
      setActiveSection('reports')
      setDetailId(null)
    }
    // Called by din-form.js after a calculation to ensure the content panel is open
    ;(window as any).dinOpenEmmissionssenkenSection = () => {
      setActiveSection('emmissionssenken')
      setDetailId(null)
      setSelectedDocId(null)
      // Signal an DinPanel: Ergebnis verfügbar → Accordion updaten
      window.dispatchEvent(new CustomEvent('din-result-ready'))
    }
    // Called by script.js context menu → opens side panels for audits/notes
    ;(window as any).openAuditsSection = () => {
      setActiveSection('audits')
      setAuditEditId(null) // Kein eingebettetes Formular — floating Dialog kommt separat
      setDetailId(null)
      setSelectedDocId(null)
    }
    ;(window as any).openNotesSection = () => {
      setActiveSection('notes')
      setDetailId(null)
      setSelectedDocId(null)
    }
    ;(window as any).openRenderSection = () => {
      setOverlaySection('punktwolke')
      // Signal RendererSettings: expand + highlight Point Budget
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('highlight-point-budget'))
      }, 180)
    }
    ;(window as any).createSceneFromViewer = (data: {
      camera: { position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number } }
      clipBox?: { position: [number, number, number]; scale: [number, number, number]; rotation: [number, number, number] } | null
      activeCloudIds: string[]
      title?: string
      // Neue erweiterte Felder (von captureFullViewerState)
      renderer?: any
      clouds?: any[]
      dataLayers?: any[]
      geoAnchor?: any
    }) => {
      const allAnnotations = useAnnotationStore.getState().annotations
      const cloudAnnotationIds = allAnnotations
        .filter(a => {
          if (!currentCloudId) return true
          if (projectCloudIds.length > 0) return projectCloudIds.includes(a.cloudId)
          return a.cloudId === currentCloudId
        })
        .map(a => a.id)

      // Vollständigen ViewerState zusammenbauen
      const viewerState: any = { camera: data.camera, clipBox: data.clipBox ?? null }
      if (data.renderer) viewerState.renderer = data.renderer
      if (data.clouds) viewerState.clouds = data.clouds
      if (data.dataLayers) viewerState.dataLayers = data.dataLayers
      if (data.geoAnchor) viewerState.geoAnchor = data.geoAnchor

      // Thumbnails VOR dem Erstellen capturen → werden mit dem POST mitgesendet (keine Race-Condition)
      const thumbs = captureViewerThumbnailPair()
      const mhThumbs = captureViewerThumbnailMediumHigh()

      const scene = sceneCreateScene({
        title: data.title ?? `Scene ${new Date().toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
        cloudId: currentCloudId ?? '',
        viewerState,
        activeCloudIds: data.activeCloudIds,
        annotationIds: cloudAnnotationIds,
        ...(thumbs ? { thumbnailSmall: thumbs.small } : {}),
        ...(mhThumbs ? { thumbnailMedium: mhThumbs.medium, thumbnailHigh: mhThumbs.high } : (thumbs ? { thumbnailMedium: thumbs.medium } : {})),
      })

      setOverlaySection('scene')
      return scene
    }
    return () => {
      delete (window as any).openDokumentenablage
      delete (window as any).createSceneFromViewer
      delete (window as any).dinOpenEmmissionssenkenSection
      delete (window as any).openAuditsSection
      delete (window as any).openNotesSection
    }
  }, [sceneCreateScene, currentCloudId])

  // Game-Controller-Popup-Status liegt im Zustand-Store auf App-Level, damit
  // eine bestehende Pairing-Session beim Projektwechsel erhalten bleibt.
  const toggleController = useControllerStore((s) => s.setOpen)

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSectionChange = useCallback((section: NavSection | null) => {
    // Controller: öffnet ein QR-Popup, kein ContentPanel; nochmal klicken = trennen
    if (section === 'controller') {
      toggleController((o) => !o)
      return
    }
    // Agent: öffnet das schwebende DIN-Chat-Fenster, kein ContentPanel
    if (section === 'agent') {
      if (typeof (window as any).dinChatOpen === 'function') {
        (window as any).dinChatOpen()   // ohne posRect → CSS-Zentrierung greift
      }
      return
    }
    // Gebäude: Start-Szene laden damit Viewer in den Ausgangszustand zurückkehrt
    if (section === 'gebaeude') {
      sceneActivateStart(currentProjectId ?? undefined)
    }
    setActiveSection(section)
    setDetailId(null)
    setSelectedDocId(null)
    setAuditEditId(null)
    // Close audit dialog + restore clip box when switching away from audits
    if (section !== 'audits' && typeof (window as any).closeAuditDialog === 'function') {
      ;(window as any).closeAuditDialog()
    }
    // Close open document editor if switching away from reports
    if (section !== 'reports' && activeDocumentId) {
      closeEditor()
    }
  }, [activeDocumentId, closeEditor, sceneActivateStart, currentProjectId])

  const handleOverlayChange = useCallback((section: NavSection | null) => {
    setOverlaySection(section)
  }, [])

  const handleBurgerClick = useCallback(() => {
    // Open Command Menu (⌘K)
    if (typeof (window as any).cmdOpen === 'function') {
      ;(window as any).cmdOpen()
    }
  }, [])

  const handleOpenReport = useCallback((docId: string) => {
    setDetailId(docId)
    openDocumentViewer(docId)
  }, [openDocumentViewer])

  const handleBackToList = useCallback(() => {
    setDetailId(null)
    if (activeDocumentId) closeEditor()
  }, [activeDocumentId, closeEditor])

  const handleCreateReport = useCallback(() => {
    const doc = createDocument('Neuer Report', currentCloudId ?? undefined)
    openDocumentEditor(doc.id)
    setDetailId(doc.id)
  }, [createDocument, openDocumentEditor, currentCloudId])

  // ── Panel-Titel und Create-Handler ───────────────────────────────────────
  const panelConfig = useMemo(() => {
    const configs: Record<NavSection, { title: string; onCreate?: () => void }> = {
      gebaeude: { title: 'Gebäudedaten', onCreate: undefined },
      reports: { title: 'Reports', onCreate: handleCreateReport },
      docs: { title: 'Docs', onCreate: undefined },
      audits: { title: 'Audits', onCreate: () => {
        // Show placement hint — user must click a point first
        setAuditEditId('placing')
      }},
      notes: { title: 'Notes', onCreate: startPlacing },
      'pointcloud-upload': { title: 'PointCloud Upload', onCreate: undefined },
      controller: { title: 'Controller', onCreate: undefined },
      agent: { title: 'Agent', onCreate: undefined },
      raumbuch: { title: 'Raumbuch', onCreate: undefined },
      emmissionssenken: { title: 'DIN Spec Berechnung', onCreate: undefined },
      scene: { title: 'Scene', onCreate: undefined },
      punktwolke: { title: 'Punktwolke', onCreate: undefined },
      whiteboard: { title: 'Whiteboard', onCreate: undefined },
      board: { title: 'Board', onCreate: undefined },
      user: { title: 'User', onCreate: undefined },
    }
    return configs
  }, [handleCreateReport, startPlacing])

  // ── Render Content Panel Innards ─────────────────────────────────────────
  function renderPanelContent() {
    if (!activeSection) return null

    switch (activeSection) {
      case 'gebaeude':
        return <GebaeudePanel />

      case 'reports':
        if (detailId && activeDocumentId) {
          // Detail-/Edit-Ansicht — DocumentEditor inline
          return (
            <DocumentEditor
              viewerRef={viewerRef}
              currentCloudId={currentCloudId}
              inline
            />
          )
        }
        return (
          <DocumentList
            currentCloudId={currentCloudId}
            projectCloudIds={projectCloudIds}
            onOpenDocument={handleOpenReport}
          />
        )

      case 'docs':
        return <PaperlessPanel
          onSelectDoc={(id, summary) => { setSelectedDocId(id); setSelectedDocSummary(summary ?? null) }}
          selectedDocId={selectedDocId}
          projectId={currentProjectId}
        />

      case 'audits':
        if (auditEditId === 'placing') {
          return (
            <AuditPlacementHint
              onPlaced={() => {
                setAuditEditId(null) // Zurück zur Liste
                // Floating Dialog öffnen
                if (typeof (window as any).showAuditDialog === 'function') {
                  ;(window as any).showAuditDialog()
                }
              }}
              onCancel={() => setAuditEditId(null)}
            />
          )
        }
        if (auditEditId) {
          return (
            <AuditFormMount
              auditId={auditEditId}
              onClose={() => setAuditEditId(null)}
            />
          )
        }
        return (
          <AuditList
            currentCloudId={currentCloudId}
            onEditAudit={(id: string) => {
              // Floating Fenster statt eingebettetes Formular
              if (typeof (window as any).editAudit === 'function') {
                (window as any).editAudit(id)
              } else {
                setAuditEditId(id)
              }
            }}
          />
        )

      case 'notes':
        return <AnnotationList viewerRef={viewerRef} currentCloudId={currentCloudId} projectCloudIds={projectCloudIds} />

      case 'pointcloud-upload':
        return <PointCloudUploadPanel />

      case 'agent':
        return <AgentChatPanel />

      case 'emmissionssenken':
        return <DinPanel />

      case 'raumbuch':
        return null // Wird als Fullscreen-Overlay gerendert (s.u.)

      case 'user':
        return (
          <div className="content-panel__placeholder">
            Benutzerprofil — in Entwicklung
          </div>
        )

      default:
        return null
    }
  }

  // ── Render Overlay Panel Content (Scene / Punktwolke) ───────────────────
  function renderOverlayContent() {
    if (!overlaySection) return null
    switch (overlaySection) {
      case 'scene':
        return <ScenePanel currentProjectId={currentProjectId} currentCloudId={currentCloudId} projectCloudIds={projectCloudIds} />
      case 'punktwolke':
        return <CloudPanel />
      default:
        return null
    }
  }

  const config = activeSection ? panelConfig[activeSection] : null
  const overlayConfig = overlaySection ? panelConfig[overlaySection] : null
  const docReaderActive = activeSection === 'docs' && !!selectedDocId
  const whiteboardActive = activeSection === 'whiteboard'
  const boardActive = activeSection === 'board'
  // ContentPanel bleibt auch bei aktivem DocReader sichtbar (auf breiten Screens)
  // Beim Whiteboard/Board wird stattdessen ein Vollbild-Overlay gezeigt → kein ContentPanel.
  const raumbuchActive = activeSection === 'raumbuch'
  const userPermissions = useAuthStore((s) => s.user?.permissions || []) as string[]
  const canSeeGebaeude = hasModuleAccess(SECTION_MODULE_MAP['gebaeude'], userPermissions)
  const gebaeudeFloating = activeSection === 'gebaeude' && canSeeGebaeude
  const showContentPanel = activeSection !== null && !whiteboardActive && !boardActive && !raumbuchActive && !gebaeudeFloating

  // Falls activeSection auf eine Section steht, für die der eingeloggte User
  // (z.B. via Access-Token) keine Permissions hat, zurücksetzen.
  useEffect(() => {
    if (!activeSection) return
    const moduleKey = SECTION_MODULE_MAP[activeSection]
    if (moduleKey && !hasModuleAccess(moduleKey, userPermissions)) {
      setActiveSection(null)
    }
  }, [activeSection, userPermissions])

  // ── Game-Controller: Mobile → Desktop Nav-Befehle empfangen ──────────────
  // GameControllerSession dispatcht orto:controller-cmd-Events mit detail.section.
  // Wir nutzen ein Ref, damit der Listener stabil registriert bleibt.
  const handleSectionChangeRef = useRef(handleSectionChange)
  useEffect(() => { handleSectionChangeRef.current = handleSectionChange }, [handleSectionChange])
  useEffect(() => {
    const onCmd = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail) return
      if (detail.type === 'nav') handleSectionChangeRef.current(detail.section as NavSection | null)
    }
    window.addEventListener('orto:controller-cmd', onCmd)
    return () => window.removeEventListener('orto:controller-cmd', onCmd)
  }, [])

  // ── Desktop → Mobile: aktuellen Section-State melden ──────────────────────
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('orto:controller-state', { detail: { activeSection } }))
  }, [activeSection])

  // Re-Publish auf explizite Anfrage (z.B. Mobile-Reconnect / neuer Pair)
  useEffect(() => {
    const onRequest = () => {
      window.dispatchEvent(new CustomEvent('orto:controller-state', { detail: { activeSection } }))
    }
    window.addEventListener('orto:controller-state-request', onRequest)
    return () => window.removeEventListener('orto:controller-state-request', onRequest)
  }, [activeSection])

  // Dock-Pill ausblenden wenn Raumbuch oder Board aktiv
  useEffect(() => {
    const dock = document.getElementById('dock-pill')
    const statusBar = document.getElementById('status-bar')
    const bottomDock = document.getElementById('bottom-dock')
    const hide = raumbuchActive || boardActive || whiteboardActive
    if (dock) dock.style.display = hide ? 'none' : ''
    if (statusBar) statusBar.style.display = hide ? 'none' : ''
    if (bottomDock) bottomDock.style.display = hide ? 'none' : ''
    return () => {
      if (dock) dock.style.display = ''
      if (statusBar) statusBar.style.display = ''
      if (bottomDock) bottomDock.style.display = ''
    }
  }, [raumbuchActive, boardActive, whiteboardActive])

  return (
    <div className="app-shell" data-component="AppShell">
      {/* ── Sidebar (always visible, collapsed/expanded) ─────────────────── */}
      <Sidebar
        activeSection={activeSection}
        overlaySection={overlaySection}
        onSectionChange={handleSectionChange}
        onOverlayChange={handleOverlayChange}
        onBurgerClick={handleBurgerClick}
      />

      {/* ── Project Title (top-left, next to sidebar) ─────────────────────── */}
      {currentProject?.name && (
        <div className="project-title-bar" data-component="ProjectTitleBar">
          <span className="project-title-bar__name">{currentProject.name}</span>
        </div>
      )}

      {/* ── Content Panel (conditional) ─────────────────────────────────────── */}
      {showContentPanel && config && (
        <ContentPanel
          section={activeSection!}
          title={config.title}
          onBack={
            detailId ? handleBackToList
            : auditEditId ? () => setAuditEditId(null)
            : null
          }
          onCreate={auditEditId ? null : (config.onCreate ?? null)}
          headerAction={
            activeSection === 'audits' && auditEditId
              ? <span className="content-panel__header-label">{auditEditId === 'placing' ? 'platzieren' : auditEditId === 'new' ? 'erstellen' : 'bearbeiten'}</span>
              : undefined
          }
        >
          {renderPanelContent()}
        </ContentPanel>
      )}

      {/* ── Gebäudedaten Floating Panel ──────────────────────────────────── */}
      {gebaeudeFloating && (
        <BuildingFloatDialog
          projectId={currentProjectId}
          onClose={() => setActiveSection(null)}
        />
      )}

      {/* Game-Controller-Popup lebt jetzt in App.tsx, damit Projektwechsel
          die Mobile-Pairing-Session nicht abreißen lassen. */}

      {/* Presence-Bar oben rechts + Cursor-/Follow-Overlay */}
      <PresenceBar />
      <ViewerSyncOverlay />

      {/* ── Overlay Panel (Scene / Render — independent floating panel) ────── */}
      {overlaySection && overlayConfig && (
        <ContentPanel
          section={overlaySection}
          title={overlayConfig.title}
          onBack={() => setOverlaySection(null)}
          className="content-panel--overlay"
        >
          {renderOverlayContent()}
        </ContentPanel>
      )}

      {/* ── Docs Reader (replaces viewer + ContentPanel when active) ────────── */}
      {docReaderActive && (
        <DocReader
          docId={selectedDocId!}
          summary={selectedDocSummary}
          onClose={() => { setSelectedDocId(null); setSelectedDocSummary(null) }}
        />
      )}

      {/* ── Whiteboard (Vollbild-Overlay, ersetzt Viewer + ContentPanel) ────── */}
      {whiteboardActive && currentProjectId && (
        <Suspense fallback={
          <div className="whiteboard-panel">
            <div className="whiteboard-panel__topbar">
              <button
                type="button"
                className="whiteboard-panel__close"
                onClick={() => setActiveSection(null)}
              >
                ← Zurück
              </button>
              <div className="whiteboard-panel__title">Whiteboard wird geladen…</div>
              <div className="whiteboard-panel__spacer" />
            </div>
            <div className="whiteboard-panel__canvas" />
          </div>
        }>
          <WhiteboardErrorBoundary>
            <WhiteboardPanel
              projectId={currentProjectId}
              projectCloudIds={projectCloudIds}
              onClose={() => setActiveSection(null)}
              onOpenScene={(sceneId) => {
                setActiveSection(null)
                requestAnimationFrame(() => {
                  try { sceneActivate(sceneId) } catch (err) { console.warn('[wb] activateScene failed', err) }
                })
              }}
              onOpenDoc={(docId) => {
                setActiveSection('docs')
                setSelectedDocId(docId)
                setSelectedDocSummary(null)
                openDocumentViewer(docId)
              }}
              onOpenReport={(reportId) => {
                setActiveSection('reports')
                setDetailId(reportId)
                openDocumentViewer(reportId)
              }}
            />
          </WhiteboardErrorBoundary>
        </Suspense>
      )}
      {whiteboardActive && !currentProjectId && (
        <div className="whiteboard-panel">
          <div className="whiteboard-panel__topbar">
            <button
              type="button"
              className="whiteboard-panel__close"
              onClick={() => setActiveSection(null)}
            >
              ← Zurück
            </button>
            <div className="whiteboard-panel__title">Whiteboard</div>
            <div className="whiteboard-panel__spacer" />
          </div>
          <div className="whiteboard-panel__canvas">
            <div className="whiteboard-panel__error">
              Bitte zuerst ein Projekt öffnen — das Whiteboard ist projektgebunden.
            </div>
          </div>
        </div>
      )}

      {/* ── Board (Clean-Room Whiteboard — Vollbild-Overlay) ── */}
      {boardActive && currentProjectId && (
        <BoardPanel
          projectId={currentProjectId}
          onClose={() => setActiveSection(null)}
        />
      )}

      {/* ── Raumbuch (Tabelle / Split / Scan — ersetzt Viewer + ContentPanel) ── */}
      {raumbuchActive && (
        <div className="raumbuch-overlay">
          <RaumbuchPanel />
        </div>
      )}

      {/* ── Overlays (above everything) ─────────────────────────────────────── */}
      <AnnotationLayer
        viewerRef={viewerRef}
        currentCloudId={currentCloudId}
        tier={tier}
        tierConfig={layerState.annotations}
        popupConfig={layerState.annotationPopups}
        visible={activeSection === 'notes' || annotationOverlayActive}
      />

      {activeAnnotationId && editorMode !== 'none' && (
        <AnnotationEditor viewerRef={viewerRef} />
      )}

      <PlanDropZone />
      <PresentationDock projectId={currentProjectId} />
      <AnimationBar />

      {zoomOverlayOpen && (
        <SemanticZoomOverlay
          ref={overlayRef}
          tier={tier}
          distance={distance}
          layerState={layerState}
          onClose={() => setZoomOverlayOpen(false)}
        />
      )}

      <CssDevEditor />
    </div>
  )
}
